import { Box, Typography } from "@mui/material"
import { AutocompleteMulti } from "components/AutocompleteMulti/AutocompleteMulti"
import { StyledButton } from "components/Button/StyledButton"
import { ReportNodeImageBox } from "components/ReportNodeImage/ReportNodeImageBox"
import { ReportNodeImageEditDialog } from "components/ReportNodeImage/ReportNodeImageEditDialog"
import { ReportNodeTableBox } from "components/ReportNodeTable/ReportNodeTableBox"
import { ReportNodeTableEditDialog } from "components/ReportNodeTable/ReportNodeTableEditDialog"
import { ReportNodeTextBox } from "components/ReportNodeText/ReportNodeTextBox"
import { ReportNodeTextEditDialog } from "components/ReportNodeText/ReportNodeTextEditDialog"
import { DataNode, Examination, NodeDataType, TextBlock } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useMemo, useState } from "react"
import { isNodeImage, isNodeTable, isNodeText } from "store/typeGuards"

type ParameterCardProps = {
  examination: Examination
  // eslint-disable-next-line no-unused-vars
  setExamination: React.Dispatch<React.SetStateAction<Examination | undefined>>
}

export const ParameterCard = ({ examination, setExamination }: ParameterCardProps) => {
  const [newNode, setNewNode] = useState<DataNode>()

  const { textBlocks } = useStoreState((state) => state)
  const { fetchTextBlocks } = useStoreActions((actions) => actions)

  useEffect(() => {
    fetchTextBlocks({ fresh: false })
  }, [fetchTextBlocks])

  const filteredTextBlocks = useMemo(
    () =>
      textBlocks.filter(
        (textBlock) =>
          textBlock.isTestRelevant &&
          (textBlock.examinations.length === 0 ||
            textBlock.examinations.includes(examination.examinationConfig.id)) &&
          (textBlock.norms.length === 0 || textBlock.norms.includes(examination.norm.id))
      ),
    [textBlocks, examination.examinationConfig.id, examination.norm.id]
  )

  const handleChangeParameter = (value: TextBlock[]) => {
    if (examination) {
      setExamination({
        ...examination,
        testParameterBlock: value,
      })
    }
  }

  const handleDeleteNode = (textBlockId: string, blockId: string) => {
    setExamination((prevState) => {
      if (!prevState) return prevState
      return {
        ...prevState,
        testParameterBlock: prevState.testParameterBlock.map((item) => {
          if (item.id === textBlockId) {
            return {
              ...item,
              block: item.block.filter((item) => item.id !== blockId),
            }
          }
          return item
        }),
      }
    })
  }

  const handleEditNode = (node: DataNode) => {
    setNewNode(node)
  }

  const handleAddNode = () => {
    if (newNode) {
      const isOldBlock = examination.testParameterBlock
        .flatMap((item) => item.block)
        .find((item) => item.id === newNode?.id)

      if (isOldBlock) {
        setExamination((prevState) => {
          if (!prevState) return prevState
          return {
            ...prevState,
            testParameterBlock: prevState.testParameterBlock.map((item) => {
              if (isOldBlock.id === newNode.id) {
                return {
                  ...item,
                  block: item.block.map((item) => (item.id === newNode.id ? newNode : item)),
                }
              }
              return item
            }),
          }
        })
        // } else {
        // TODO: Add new block
        // }
        setNewNode(undefined)
      }
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography>Parameter</Typography>

      <AutocompleteMulti
        options={filteredTextBlocks}
        value={examination.testParameterBlock}
        onChange={(_, value) => handleChangeParameter(value)}
        label="Parameter hinzufügen"
        fullWidth
      />

      <Box>
        {examination.testParameterBlock.map((textBlock) => (
          <Box key={textBlock.id}>
            {textBlock.block.map((item) => (
              <Box
                key={item.id}
                sx={{
                  gap: 1,
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                {isNodeText(item) && <ReportNodeTextBox data={item} />}
                {isNodeTable(item) && <ReportNodeTableBox data={item} />}
                {isNodeImage(item) && <ReportNodeImageBox data={item} isSmall={true} />}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <StyledButton
                    buttonType="edit"
                    size="small"
                    iconOnly={true}
                    onClick={() => handleEditNode(item)}
                  />
                  <StyledButton
                    buttonType="delete"
                    size="small"
                    iconOnly={true}
                    onClick={() => handleDeleteNode(textBlock.id, item.id)}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      {newNode && isNodeText(newNode) && (
        <ReportNodeTextEditDialog
          open={newNode.dataType === NodeDataType.TEXT}
          onClose={() => setNewNode(undefined)}
          node={newNode}
          setNode={setNewNode}
          onSave={handleAddNode}
        />
      )}

      {newNode && isNodeTable(newNode) && (
        <ReportNodeTableEditDialog
          open={newNode.dataType === NodeDataType.TABLE}
          onClose={() => setNewNode(undefined)}
          node={newNode}
          setNode={setNewNode}
          onSave={handleAddNode}
        />
      )}

      {newNode && isNodeImage(newNode) && (
        <ReportNodeImageEditDialog
          open={newNode.dataType === NodeDataType.IMAGE}
          onClose={() => setNewNode(undefined)}
          node={newNode}
          setNode={setNewNode}
          onSave={handleAddNode}
        />
      )}
    </Box>
  )
}
